import { useEffect, useState } from 'react';
import { CircularProgress } from '@mui/material';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from 'src/@/components/ui/card';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from 'src/@/components/ui/select';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from 'src/@/components/ui/tooltip';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from 'src/@/components/ui/dialog';
import Iconify from 'src/components/Iconify';
import v3_actionGetChannelHealth from 'src/actions/v3/chats/v3_actionGetChannelHealth';
import v3_actionGetMessagesWithoutInteraction from 'src/actions/v3/chats/v3_actionGetMessagesWithoutInteraction';

// ----------------------------------------------------------------------

const healthStatusColors = {
    excelente: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200' },
    bom: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200' },
    regular: { bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-200' },
    ruim: { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200' },
    critico: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200' },
};

const healthStatusLabels = {
    excelente: 'Excelente',
    bom: 'Bom',
    regular: 'Regular',
    ruim: 'Ruim',
    critico: 'Crítico',
};

function MetricCard({ title, value, subtitle, icon, trend, tooltip }) {
    const cardContent = (
        <CardContent className="p-4 h-full flex flex-col justify-center">
            <div className="flex items-center justify-between">
                <div className="flex-1">
                    <div className="flex items-center gap-1">
                        <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">{title}</p>
                        {tooltip && <Iconify icon="mdi:information-outline" className="text-gray-400 w-3.5 h-3.5" />}
                    </div>
                    <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
                    {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
                </div>
                {icon && (
                    <div className="w-10 h-10 rounded-full bg-violet-50 flex items-center justify-center">
                        <Iconify icon={icon} className="text-violet-600 w-5 h-5" />
                    </div>
                )}
            </div>
        </CardContent>
    );

    if (tooltip) {
        return (
            <Card className="border border-gray-100 shadow-sm transition-all hover:shadow-md cursor-help">
                <TooltipProvider>
                    <Tooltip delayDuration={300}>
                        <TooltipTrigger asChild>
                            {cardContent}
                        </TooltipTrigger>
                        <TooltipContent className="max-w-[220px] text-center bg-slate-800 text-white border-slate-700">
                            <p>{tooltip}</p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            </Card>
        );
    }

    return (
        <Card className="border border-gray-100 shadow-sm">
            {cardContent}
        </Card>
    );
}

function HealthScoreGauge({ score, status }) {
    const colors = healthStatusColors[status] || healthStatusColors.regular;
    const circumference = 2 * Math.PI * 45;
    const strokeDashoffset = circumference - (score / 100) * circumference;

    return (
        <div className="flex flex-col items-center justify-center">
            <span className={`mt-2 px-3 py-1 rounded-full text-md font-medium ${colors.bg} ${colors.text}`}>
                {healthStatusLabels[status] || status}
            </span>
            <span className="text-xs text-gray-500">Saude</span>

        </div>
    );
}

function TrendChart({ data }) {
    if (!data || data.length === 0) return null;

    const maxTotal = Math.max(...data.map(d => d.total), 1);

    return (
        <div className="mt-4">
            <p className="text-sm font-medium text-gray-700 mb-3">Tendência Diária</p>
            <div className="flex items-end gap-1 h-24">
                {data.slice(-14).map((day, index) => (
                    <div key={index} className="flex-1 flex flex-col items-center gap-1">
                        <div className="w-full flex flex-col gap-0.5" style={{ height: '80px' }}>
                            <div
                                className="w-full bg-violet-500 rounded-t transition-all"
                                style={{ height: `${(day.respondidas / maxTotal) * 100}%` }}
                                title={`Respondidas: ${day.respondidas}`}
                            />
                            <div
                                className="w-full bg-gray-200 rounded-b transition-all"
                                style={{ height: `${((day.total - day.respondidas) / maxTotal) * 100}%` }}
                                title={`Não respondidas: ${day.total - day.respondidas}`}
                            />
                        </div>
                        <span className="text-[9px] text-gray-400 truncate w-full text-center">
                            {day.data?.slice(5)}
                        </span>
                    </div>
                ))}
            </div>
            <div className="flex items-center gap-4 mt-2 justify-center">
                <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-violet-500 rounded" />
                    <span className="text-xs text-gray-500">Respondidas</span>
                </div>
                <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-gray-200 rounded" />
                    <span className="text-xs text-gray-500">Sem resposta</span>
                </div>
            </div>
        </div>
    );
}

function formatDateTime(value) {
    if (!value) return '-';
    try {
        return new Date(value).toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch (e) {
        return '-';
    }
}

function NoInteractionModal({ open, onOpenChange, clienteId, periodo, total }) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [items, setItems] = useState([]);
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState({ total: 0, pages: 0 });

    async function fetchData(targetPage = 1) {
        if (!clienteId) return;
        try {
            setLoading(true);
            setError(null);
            const response = await v3_actionGetMessagesWithoutInteraction(clienteId, {
                periodo,
                page: targetPage,
                limit: 20,
            });
            if (response.data?.error === false) {
                setItems(response.data.data || []);
                setPagination(response.data.pagination || { total: 0, pages: 0 });
                setPage(targetPage);
            } else {
                setError(response.data?.mensagem || 'Erro ao carregar dados');
            }
        } catch (err) {
            console.error('Erro ao buscar contatos sem interação:', err);
            setError('Erro ao carregar contatos');
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (open) {
            fetchData(1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, clienteId, periodo]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl p-0 overflow-hidden gap-0">
                <DialogHeader className="px-6 py-4 border-b">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
                            <Iconify icon="mdi:account-alert-outline" className="text-red-600 w-5 h-5" />
                        </div>
                        <div className="text-left">
                            <DialogTitle className="text-base font-semibold text-gray-900">
                                Contatos sem interação prévia
                            </DialogTitle>
                            <DialogDescription className="text-xs text-gray-500 mt-0.5">
                                {total || 0} mensagens enviadas para números que nunca interagiram
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>

                <div className="max-h-[60vh] overflow-y-auto">
                    {loading ? (
                        <div className="flex items-center justify-center py-16">
                            <CircularProgress size={28} />
                        </div>
                    ) : error ? (
                        <div className="flex flex-col items-center justify-center py-16 text-gray-500">
                            <Iconify icon="mdi:alert-circle-outline" className="w-10 h-10 mb-2 text-orange-400" />
                            <p className="text-sm">{error}</p>
                            <button
                                onClick={() => fetchData(page)}
                                className="mt-3 text-sm text-violet-600 hover:underline"
                            >
                                Tentar novamente
                            </button>
                        </div>
                    ) : items.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 text-gray-500">
                            <Iconify icon="mdi:account-check-outline" className="w-10 h-10 mb-2 text-gray-300" />
                            <p className="text-sm font-medium">Nenhum contato sem interação</p>
                            <p className="text-xs text-gray-400 mt-1">Não há mensagens frias neste período</p>
                        </div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="sticky top-0 bg-gray-50 border-b">
                                <tr className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                    <th className="px-6 py-3">Contato</th>
                                    <th className="px-3 py-3 text-center">Mensagens</th>
                                    <th className="px-3 py-3 text-center">Respondidas</th>
                                    <th className="px-3 py-3 text-center">Fora 24h</th>
                                    <th className="px-6 py-3 text-right">Última mensagem</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {items.map((item, index) => (
                                    <tr key={item.phone || index} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-3">
                                            <div className="flex flex-col">
                                                <span className="font-medium text-gray-900">
                                                    {item.nome_cliente || 'Sem nome'}
                                                </span>
                                                <span className="text-xs text-gray-400">{item.phone}</span>
                                            </div>
                                        </td>
                                        <td className="px-3 py-3 text-center font-semibold text-gray-900">
                                            {item.total_mensagens}
                                        </td>
                                        <td className="px-3 py-3 text-center">
                                            <span className={item.respondidas > 0 ? 'text-green-600 font-medium' : 'text-gray-400'}>
                                                {item.respondidas}
                                            </span>
                                        </td>
                                        <td className="px-3 py-3 text-center">
                                            <span className={item.fora_24h > 0 ? 'text-orange-600 font-medium' : 'text-gray-400'}>
                                                {item.fora_24h}
                                            </span>
                                        </td>
                                        <td className="px-6 py-3 text-right text-xs text-gray-500">
                                            {formatDateTime(item.ultima_mensagem)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {!loading && !error && pagination.pages > 1 && (
                    <div className="flex items-center justify-between px-6 py-3 border-t bg-gray-50">
                        <span className="text-xs text-gray-500">
                            Página {page} de {pagination.pages} · {pagination.total} contatos
                        </span>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => fetchData(page - 1)}
                                disabled={page <= 1}
                                className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-600 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            >
                                Anterior
                            </button>
                            <button
                                onClick={() => fetchData(page + 1)}
                                disabled={page >= pagination.pages}
                                className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-200 text-gray-600 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            >
                                Próxima
                            </button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default function ChannelHealth({ canal, title, description, hiddenHeadaer }) {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [healthData, setHealthData] = useState(null);
    const [periodo, setPeriodo] = useState('30d');
    const [noInteractionModalOpen, setNoInteractionModalOpen] = useState(false);

    const clienteId = canal?.cliente_id || canal?._id;

    async function fetchHealthData() {
        if (!clienteId) {
            setError('Canal não configurado');
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);
            const response = await v3_actionGetChannelHealth(clienteId, { periodo });
            if (response.data?.error === false) {
                setHealthData(response.data.data);
            } else {
                setError(response.data?.mensagem || 'Erro ao carregar dados');
            }
        } catch (err) {
            console.error('Erro ao buscar dados de saúde:', err);
            setError('Erro ao carregar dados de saúde do canal');
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        fetchHealthData();
    }, [clienteId, periodo]);

    if (!clienteId) {
        return (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                <Iconify icon="mdi:heart-pulse" className="w-12 h-12 mb-3 text-gray-300" />
                <p className="text-sm">Configure o canal para monitorar a saúde</p>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <CircularProgress size={32} />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                <Iconify icon="mdi:alert-circle-outline" className="w-12 h-12 mb-3 text-orange-400" />
                <p className="text-sm">{error}</p>
                <button
                    onClick={fetchHealthData}
                    className="mt-3 text-sm text-violet-600 hover:underline"
                >
                    Tentar novamente
                </button>
            </div>
        );
    }

    if (!healthData || healthData.health_status === 'sem_dados') {
        return (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                <Iconify icon="mdi:chart-line" className="w-12 h-12 mb-3 text-gray-300" />
                <p className="text-sm font-medium">Nenhum dado disponível</p>
                <p className="text-xs text-gray-400 mt-1">Não há mensagens registradas neste período</p>
            </div>
        );
    }

    const { resumo, tempo_resposta, health_score, health_status, por_tipo_mensagem, tendencia_diaria } = healthData;

    return (
        <div className="space-y-6">
            {/* Header com seletor de período */}
            {!hiddenHeadaer && <div className="flex items-center justify-between">
                <div>
                    <h3 className="text-lg font-semibold text-gray-900">Mensagens enviadas para contatos frios</h3>
                    <p className="text-sm text-gray-500">Métricas de engajamento e qualidade das mensagens enviadas fora da janela de 24/sem interação</p>
                </div>
                <Select value={periodo} onValueChange={setPeriodo}>
                    <SelectTrigger className="w-32">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="30d">Últimos 30 dias</SelectItem>
                    </SelectContent>
                </Select>
            </div>}

            {resumo?.mensagens_sem_interacao_cliente > 100 && (
                <div className={`flex items-start gap-3 p-4 border rounded-lg ${resumo?.taxa_resposta_sem_interacao_cliente < 10 ? 'bg-red-50 border-red-200' : 'bg-orange-50 border-orange-200'}`}>
                    <Iconify
                        icon={resumo?.taxa_resposta_sem_interacao_cliente < 10 ? "mdi:alert-decagram" : "mdi:alert"}
                        className={`w-5 h-5 flex-shrink-0 mt-0.5 ${resumo?.taxa_resposta_sem_interacao_cliente < 10 ? 'text-red-600' : 'text-orange-500'}`}
                    />
                    <div>
                        <p className={`text-sm font-medium ${resumo?.taxa_resposta_sem_interacao_cliente < 10 ? 'text-red-900' : 'text-orange-900'}`}>
                            {resumo?.taxa_resposta_sem_interacao_cliente < 10
                                ? "Risco Crítico: Alto volume de mensagens frias sem resposta"
                                : "Alerta de Risco: Alto volume de mensagens sem interação prévia"
                            }
                        </p>
                        <p className={`text-xs mt-1 ${resumo?.taxa_resposta_sem_interacao_cliente < 10 ? 'text-red-700' : 'text-orange-700'}`}>
                            {resumo?.mensagens_sem_interacao_cliente} mensagens enviadas para contatos novos/frios.
                            {resumo?.taxa_resposta_sem_interacao_cliente < 10
                                ? ` A taxa de resposta é muito baixa (${resumo?.taxa_resposta_sem_interacao_cliente}%). O envio em massa sem engajamento é o principal motivo de bloqueio.`
                                : ` A taxa de resposta é de ${resumo?.taxa_resposta_sem_interacao_cliente}%. Mantenha o monitoramento para evitar bloqueios.`
                            }
                        </p>
                    </div>
                </div>
            )}

            {/* Score principal */}
            <Card className="border border-gray-100 shadow-sm">
                <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row items-center gap-8">
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <div className="cursor-help">
                                        <HealthScoreGauge score={health_score} status={health_status} />
                                    </div>
                                </TooltipTrigger>
                                <TooltipContent className="bg-slate-800 text-white border-slate-700">
                                    <p>Indicador geral da saúde do canal (0-100)</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>

                        <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-4">
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <div className="text-center cursor-help p-2 rounded hover:bg-gray-50 transition-colors">
                                            <p className="text-2xl font-bold text-gray-900">{resumo?.total_mensagens || 0}</p>
                                            <p className="text-xs text-gray-500 flex items-center justify-center gap-1">
                                                Total Mensagens
                                                <Iconify icon="mdi:information-outline" className="w-3.5 h-3.5" />
                                            </p>
                                        </div>
                                    </TooltipTrigger>
                                    <TooltipContent className="bg-slate-800 text-white border-slate-700">
                                        <p>Total de mensagens enviadas e receberias no período</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>

                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <div className="text-center cursor-help p-2 rounded hover:bg-gray-50 transition-colors">
                                            <p className="text-2xl font-bold text-green-600">{resumo?.taxa_resposta_geral?.toFixed(1) || 0}%</p>
                                            <p className="text-xs text-gray-500 flex items-center justify-center gap-1">
                                                Taxa Resposta
                                                <Iconify icon="mdi:information-outline" className="w-3.5 h-3.5" />
                                            </p>
                                            <p className="text-xs text-gray-400">Resp: {resumo?.total_respostas || 0}</p>
                                        </div>
                                    </TooltipTrigger>
                                    <TooltipContent className="bg-slate-800 text-white border-slate-700">
                                        <p>Percentual de mensagens de clientes respondidas</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>

                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <div className="text-center cursor-help p-2 rounded hover:bg-gray-50 transition-colors">
                                            <p className="text-2xl font-bold text-yellow-600">{resumo?.mensagens_fora_24h || 0}</p>
                                            <p className="text-xs text-gray-500 flex items-center justify-center gap-1">
                                                Fora 24h
                                                <Iconify icon="mdi:information-outline" className="w-3.5 h-3.5" />
                                            </p>
                                        </div>
                                    </TooltipTrigger>
                                    <TooltipContent className="bg-slate-800 text-white border-slate-700">
                                        <p>Mensagens enviadas após 24h da última interação do cliente</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>

                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <div
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => resumo?.mensagens_sem_interacao_cliente > 0 && setNoInteractionModalOpen(true)}
                                            onKeyDown={(e) => {
                                                if ((e.key === 'Enter' || e.key === ' ') && resumo?.mensagens_sem_interacao_cliente > 0) {
                                                    e.preventDefault();
                                                    setNoInteractionModalOpen(true);
                                                }
                                            }}
                                            className={`text-center p-2 rounded transition-colors ${resumo?.mensagens_sem_interacao_cliente > 0 ? 'cursor-pointer hover:bg-red-50' : 'cursor-help hover:bg-gray-50'}`}
                                        >
                                            <p className="text-2xl font-bold text-red-600">{resumo?.mensagens_sem_interacao_cliente || 0}</p>
                                            <p className="text-xs text-gray-500 flex items-center justify-center gap-1">
                                                Sem Interação Cliente
                                                <Iconify
                                                    icon={resumo?.mensagens_sem_interacao_cliente > 0 ? 'mdi:open-in-new' : 'mdi:information-outline'}
                                                    className="w-3.5 h-3.5 text-red-400"
                                                />
                                            </p>
                                        </div>
                                    </TooltipTrigger>
                                    <TooltipContent className="bg-slate-800 text-white border-slate-700">
                                        <p>
                                            {resumo?.mensagens_sem_interacao_cliente > 0
                                                ? 'ALTO RISCO: clique para ver os contatos que nunca interagiram'
                                                : 'ALTO RISCO: Mensagens ativas enviadas para números que nunca interagiram'}
                                        </p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Métricas detalhadas */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <MetricCard
                    title="Taxa Resp. (Sem Interação)"
                    value={`${resumo?.taxa_resposta_sem_interacao_cliente?.toFixed(1) || 0}%`}
                    subtitle={`Resp: ${resumo?.respostas_sem_interacao_cliente || 0}`}
                    icon="mdi:percent-outline"
                    tooltip="Taxa de resposta para mensagens enviadas sem interação prévia (frias)"
                />
                <MetricCard
                    title="Taxa Resposta (Fora 24h)"
                    value={`${resumo?.taxa_resposta_fora_24h?.toFixed(1) || 0}%`}
                    subtitle={`Resp: ${resumo?.respostas_fora_24h || 0}`}
                    icon="mdi:alert-outline"
                    tooltip="Percentual de respostas em mensagens enviadas fora da janela de 24h (risco de bloqueio)"
                />
                <MetricCard
                    title="Tempo Médio Resposta"
                    value={`${tempo_resposta?.medio_minutos?.toFixed(0) || 0} min`}
                    subtitle={`Min: ${tempo_resposta?.minimo_minutos || 0} / Max: ${tempo_resposta?.maximo_minutos || 0}`}
                    icon="mdi:clock-outline"
                    tooltip="Tempo médio entre a mensagem do cliente e a resposta do atendente/bot"
                />
            </div>

            {/* Gráfico de tendência */}
            {tendencia_diaria && tendencia_diaria.length > 0 && (
                <Card className="border border-gray-100 shadow-sm">
                    <CardContent className="p-4">
                        <TrendChart data={tendencia_diaria} />
                    </CardContent>
                </Card>
            )}

            {/* Métricas por tipo de mensagem */}
            {por_tipo_mensagem && por_tipo_mensagem.length > 0 && (
                <Card className="border border-gray-100 shadow-sm">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium">Por Tipo de Mensagem</CardTitle>
                        <CardDescription className="text-xs">Distribuição e taxa de resposta por tipo</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {por_tipo_mensagem.map((tipo, index) => (
                                <div key={index} className="flex items-center gap-3">
                                    <div className="w-20 text-xs text-gray-600 font-medium capitalize">
                                        {tipo.tipo || 'Outros'}
                                    </div>
                                    <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-violet-500 rounded-full transition-all"
                                            style={{ width: `${tipo.taxa_resposta}%` }}
                                        />
                                    </div>
                                    <TooltipProvider>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <div className="w-20 text-xs text-gray-500 text-right cursor-help hover:text-gray-900 transition-colors flex items-center justify-end gap-1">
                                                    {tipo.respondidas}/{tipo.total} ({tipo.taxa_resposta}%)
                                                    <Iconify icon="mdi:information-outline" className="w-3 h-3 flex-shrink-0" />
                                                </div>
                                            </TooltipTrigger>
                                            <TooltipContent className="bg-slate-800 text-white border-slate-700">
                                                <p>Respondidas / Total (Taxa %)</p>
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Aviso sobre janela 24h */}
            {/* Aviso sobre mensagens sem interação */}
            {/* Aviso sobre mensagens sem interação */}

            <NoInteractionModal
                open={noInteractionModalOpen}
                onOpenChange={setNoInteractionModalOpen}
                clienteId={clienteId}
                periodo={periodo}
                total={resumo?.mensagens_sem_interacao_cliente || 0}
            />

        </div>
    );
}
